import React, { createContext, useContext, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { STORAGE_KEY_FAVORITES } from "../constants";

const FavoritesContext = createContext();

/**
 * Custom hook to use the FavoritesContext
 * @returns {Object} The favorites context value
 * @throws {Error} When used outside of a FavoritesProvider
 */
export const useFavoritesContext = () => {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error(
      "useFavoritesContext must be used within a FavoritesProvider",
    );
  }
  return context;
};

/**
 * FavoritesProvider component that manages user favorites state
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {React.ReactElement} FavoritesProvider component
 */
export const FavoritesProvider = ({ children }) => {
  const [favorites, setFavorites] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY_FAVORITES) || "[]");
    } catch (error) {
      console.error("Failed to parse favorites from localStorage:", error);
      return [];
    }
  });

  /**
   * Toggles the favorite status of a course
   * @param {string|number} courseId - The ID of the course to toggle
   */
  const toggleFavorite = useCallback((courseId) => {
    setFavorites((prev) => {
      const newFavorites = prev.includes(courseId)
        ? prev.filter((id) => id !== courseId)
        : [...prev, courseId];

      try {
        localStorage.setItem(
          STORAGE_KEY_FAVORITES,
          JSON.stringify(newFavorites),
        );
      } catch (error) {
        console.error("Failed to save favorites to localStorage:", error);
      }

      return newFavorites;
    });
  }, []);

  /**
   * Checks if a course is in the user's favorites
   * @param {string|number} courseId - The ID of the course to check
   * @returns {boolean} True if the course is favorited, false otherwise
   */
  const isFavorite = useCallback(
    (courseId) => {
      return favorites.includes(courseId);
    },
    [favorites],
  );

  const value = {
    favorites,
    toggleFavorite,
    isFavorite,
  };

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
};

FavoritesProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
